/**
 * The targets the organization shares with its own workspaces (`policy.
 * sharedTargets`, `org/shared-targets.ts` in contracts; owner's decision Q10 of
 * Claude Design « Proposition 26 octobre - Profils d'environnement »), under
 * Network beside the rules on targets: a staging, a preprod every member's
 * sessions can run on. Each one is named, says how the agent reaches it and
 * whether it is Production, lists the hosts it adds and its secrets — by name
 * only. A value is never typed here: this document is read by every member,
 * and each member sets each value on their own computer, where the keychain
 * seals it. On a member's machine a shared target is locked, with this
 * organization's name, and offered inert, saying why, while one of its secrets
 * has no value there.
 *
 * Published with the rest of the settings — refused by the Worker with the
 * same validator (`parseSharedTargets`), its sentences in the publish's list.
 */
import type React from 'react';
import { EMPTY_ORG_POLICY, policyOf, type OrgPolicy } from '@airlock/contracts/org/policy';
import type { OrgSharedTarget } from '@airlock/contracts/org/shared-targets';
import { MAX_TARGETS, SECRET_NAME } from '@airlock/contracts/settings/targets';
import { GhostButton, Input, RowAction } from '../../components/primitives';
import { SettingSelect, SettingToggle } from '../../components/settings-row';
import { color, font } from '../../theme/tokens';
import type { OrgSettings } from '../../lib/types';
import { DomainList, ListBlock } from './list-settings';

const MODES = [
  { value: 'direct', label: 'Direct access — the agent calls it from its micro-VM' },
  { value: 'host', label: 'Through the host — the agent asks, a person runs each read' }
];

/** A secret is sent toward every host of a direct-access target, and toward none through the host. */
function withSecretHosts(target: OrgSharedTarget): OrgSharedTarget {
  const hosts = target.mode === 'direct' ? target.hosts : [];
  return { ...target, secrets: target.secrets.map((one) => ({ ...one, hosts })) };
}

function newId(): string {
  return `shared-${Math.random().toString(36).slice(2, 10).padEnd(8, '0')}`;
}

const label: React.CSSProperties = { fontSize: 12, fontWeight: 500, color: color.inkBody, minWidth: 96 };

function SharedTarget({
  target,
  editable,
  onChange,
  onRemove
}: {
  target: OrgSharedTarget;
  editable: boolean;
  onChange: (next: OrgSharedTarget) => void;
  onRemove: () => void;
}): React.JSX.Element {
  const put = (patch: Partial<OrgSharedTarget>): void => onChange(withSecretHosts({ ...target, ...patch }));
  return (
    <div
      aria-label={`Shared target ${target.name || 'unnamed'}`}
      style={{ padding: '12px 0', boxShadow: `inset 0 -1px 0 ${color.hairSoft}`, display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={label}>Name</span>
        {editable ? (
          <Input
            aria-label="Target name"
            value={target.name}
            placeholder="Staging"
            onChange={(event) => put({ name: event.target.value })}
            style={{ maxWidth: 280 }}
          />
        ) : (
          <span style={{ fontSize: 13, color: color.ink }}>{target.name}</span>
        )}
        <span style={{ flex: 1 }} />
        {editable && (
          <RowAction danger onClick={onRemove}>
            Remove
          </RowAction>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={label}>Reached</span>
        {editable ? (
          <SettingSelect value={target.mode} options={MODES} onChange={(mode) => put({ mode: mode as OrgSharedTarget['mode'] })} wide />
        ) : (
          <span style={{ fontSize: 13, color: color.ink }}>{MODES.find((one) => one.value === target.mode)?.label}</span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={label}>Production</span>
        {editable ? (
          <SettingToggle on={target.production} onToggle={() => put({ production: !target.production })} />
        ) : (
          <span style={{ fontSize: 13, color: color.ink }}>{target.production ? 'Yes' : 'No'}</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <span style={{ ...label, paddingTop: 9 }}>Hosts</span>
        <div style={{ flex: 1 }}>
          <DomainList value={target.hosts} onChange={(hosts) => put({ hosts })} editable={editable} placeholder="api.staging.example.com" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <span style={{ ...label, paddingTop: 9 }}>Secrets</span>
        <div style={{ flex: 1 }}>
          <DomainList
            value={target.secrets.map((one) => one.name)}
            onChange={(names) => put({ secrets: names.map((name) => ({ name, hosts: [] })) })}
            editable={editable}
            placeholder="STAGING_TOKEN"
            max={32}
            problemOf={(name) =>
              SECRET_NAME.test(name) ? null : 'Letters, digits, dots, dashes and underscores, starting with a letter or a digit.'
            }
          />
          <p style={{ margin: '6px 0 0', fontSize: 12, color: color.inkFaint, fontFamily: font.sans }}>
            Names only. Each member sets each value on their own computer, sealed in its keychain — it never reaches
            this page.
            {target.mode === 'direct' ? ' Swapped into the requests sent to this target’s hosts.' : ''}
          </p>
        </div>
      </div>
    </div>
  );
}

export function SharedTargets({
  settings,
  editable,
  onChange
}: {
  settings: OrgSettings;
  editable: boolean;
  onChange: (next: OrgPolicy) => void;
}): React.JSX.Element {
  const policy = policyOf(settings);
  // The draft as typed, not as a machine would read it: a target still unnamed is kept until the publish refuses it.
  const targets = settings.policy?.sharedTargets ?? policy.sharedTargets;
  const put = (next: OrgSharedTarget[]): void => onChange({ ...EMPTY_ORG_POLICY, ...policy, sharedTargets: next });
  return (
    <ListBlock
      label="Shared targets"
      hint="Targets every member finds in this organization’s workspaces, locked, with its name: a staging, a preprod. Until a member has set a secret’s value on their computer, the target is offered there without being pickable, saying which value is missing."
    >
      {targets.map((target, i) => (
        <SharedTarget
          key={target.id}
          target={target}
          editable={editable}
          onChange={(next) => put(targets.map((one, j) => (j === i ? next : one)))}
          onRemove={() => put(targets.filter((_, j) => j !== i))}
        />
      ))}
      {!targets.length && <p style={{ margin: '4px 0 0', fontSize: 12.5, color: color.inkFaint }}>No target shared.</p>}
      {editable && targets.length < MAX_TARGETS && (
        <div style={{ marginTop: 10 }}>
          <GhostButton
            onClick={() => put([...targets, { id: newId(), name: '', production: false, mode: 'direct', hosts: [], secrets: [] }])}
          >
            Share a target
          </GhostButton>
        </div>
      )}
    </ListBlock>
  );
}
